import { COLORS } from '../../Styles/Colors';
import { Kit } from '../../Types';
import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@mui/material';
import useGetCustomData from '../../Hooks/useGetCustomData';
import { LocationSelect } from '../../Components/Select/LocationSelect';
import { Asset } from '../AssetPage';
import { SearchIcon } from 'lucide-react';
import { grey } from '@mui/material/colors';
import { Close } from '@mui/icons-material';
import {
  Form,
  FormSection,
  SectionTitle,
  SectionDescription,
  FormGrid,
  FormTextField,
  FormSelect,
  selectFocusSx,
} from '../../Components/Form';

export interface FleetFormInput {
  description: string;
  license_plate: string;
  gps_device_id: string;
  name: '';
  ovid?: number;
}

export const FleetForm: React.FC<{
  onSubmit: (data: FleetFormInput) => void;
  onCancel: () => void;
  defaultValues?: any;
  ovs: any[];
  assets?: Asset[];
  kits?: Kit[];
}> = ({ onSubmit, onCancel, defaultValues, ovs, assets, kits }) => {
  const [formData, setFormData] = useState<FleetFormInput>({
    description: '',
    license_plate: '',
    gps_device_id: '',
    name: '',
  });

  const [selectedOvs, setSelectedOvs] = useState<string | number>('');
  const [selectedAssets, setSelectedAssets] = useState<Asset[]>([]);
  const [selectedKits, setSelectedKits] = useState<Kit[]>([]);
  const [assetSearchTerm, setAssetSearchTerm] = useState('');
  const [kitSearchTerm, setKitSearchTerm] = useState('');
  const [attachmentType, setAttachmentType] = useState<'asset' | 'kit' | ''>('');

  const [devices] = useGetCustomData(`/api/gps/devices/ovs/${selectedOvs || defaultValues?.OVID || ''}`);
  const [selectedDevice, setSelectedDevice] = useState(defaultValues?.gps_device_id || '');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (defaultValues) {
      setFormData({
        description: defaultValues.description || '',
        license_plate: defaultValues.license_plate || '',
        gps_device_id: defaultValues.gps_device_id || '',
        name: defaultValues.name || '',
      });
      setSelectedOvs(defaultValues.OVID || '');
      setSelectedDevice(defaultValues.gps_device_id || '');

      if (defaultValues.assets && defaultValues.assets.length > 0) {
        setSelectedAssets(defaultValues.assets.map((asset: any) => ({
          id: asset.id,
          name: asset.name,
          serial_number: asset.serial_number,
          OVID: asset.OVID || defaultValues.OVID,
        })));
        setAttachmentType('asset');
      }

      if (defaultValues.kits && defaultValues.kits.length > 0) {
        setSelectedKits(defaultValues.kits.map((kit: any) => ({
          id: kit.id,
          name: kit.name,
          description: kit.description,
          OVID: kit.OVID || defaultValues.OVID,
        })));
        if (!defaultValues.assets || defaultValues.assets.length === 0) {
          setAttachmentType('kit');
        }
      }
    }
  }, [defaultValues]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDeviceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const deviceId = e.target.value;
    setSelectedDevice(deviceId);
    setFormData((prev) => ({ ...prev, gps_device_id: deviceId }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    const submitData = {
      ...formData,
      ...(selectedAssets.length > 0 && { asset_ids: selectedAssets.map((a) => a.id) }),
      ...(selectedKits.length > 0 && { kit_ids: selectedKits.map((k) => k.id) }),
    };
    try {
      const maybePromise: any = onSubmit(submitData);
      if (maybePromise && typeof maybePromise.then === 'function') {
        await maybePromise;
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredAssets = assets?.filter((asset) => {
    if (selectedAssets.some((selected) => selected.id === asset.id)) return true;
    return asset.OVID === Number(selectedOvs);
  }) || [];

  const filteredKits = kits?.filter((kit) => {
    if (selectedKits.some((selected) => selected.id === kit.id)) return true;
    return Number(kit.OVID) === Number(selectedOvs);
  }) || [];

  const searchFilteredAssets = assetSearchTerm.trim() === ''
    ? filteredAssets
    : filteredAssets.filter((asset) =>
        asset.name.toLowerCase().includes(assetSearchTerm.toLowerCase()) ||
        (asset.serial_number && asset.serial_number.toLowerCase().includes(assetSearchTerm.toLowerCase()))
      );

  const searchFilteredKits = kitSearchTerm.trim() === ''
    ? filteredKits
    : filteredKits.filter((kit) =>
        kit.name.toLowerCase().includes(kitSearchTerm.toLowerCase()) ||
        (kit.description && kit.description.toLowerCase().includes(kitSearchTerm.toLowerCase()))
      );

  const handleAddAsset = (asset: Asset) => {
    if (!selectedAssets.some((a) => a.id === asset.id)) {
      setSelectedAssets((prev) => [...prev, asset]);
    }
  };

  const handleRemoveAsset = (assetId: number | string) => {
    setSelectedAssets((prev) => prev.filter((asset) => asset.id !== assetId));
  };

  const handleAddKit = (kit: Kit) => {
    if (!selectedKits.some((k) => k.id === kit.id)) {
      setSelectedKits((prev) => [...prev, kit]);
    }
  };

  const handleRemoveKit = (kitId: number | string) => {
    setSelectedKits((prev) => prev.filter((kit) => kit.id !== kitId));
  };

  const renderPickerList = (
    items: any[],
    selected: any[],
    onAdd: (item: any) => void,
    onRemove: (id: number | string) => void,
    searchTerm: string,
    onSearchChange: (v: string) => void,
    searchLabel: string,
    emptyLabel: string,
    renderSecondary: (item: any) => React.ReactNode,
  ) => (
    <Box sx={{ display: 'flex', gap: 2 }}>
      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle2" gutterBottom>Available</Typography>
        <FormTextField
          label={searchLabel}
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon size={16} /></InputAdornment>
            ),
          }}
          sx={{ mb: 1 }}
        />
        <Paper sx={{ height: 250, overflow: 'auto', borderRadius: 2, boxShadow: 2 }}>
          <List dense sx={{ p: 1 }}>
            {items.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="textSecondary">{emptyLabel}</Typography>
              </Box>
            ) : (
              items.map((item) => {
                if (selected.some((s) => s.id === item.id)) return null;
                return (
                  <ListItem key={item.id} sx={{ border: `1px solid ${grey[300]}`, borderRadius: 1, mb: 0.5 }}>
                    <ListItemText primary={item.name} secondary={renderSecondary(item)} />
                    <Button variant="contained" size="small" sx={{ backgroundColor: COLORS.teal, color: '#fff' }} onClick={() => onAdd(item)}>
                      Add
                    </Button>
                  </ListItem>
                );
              })
            )}
          </List>
        </Paper>
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle2" gutterBottom>Selected ({selected.length})</Typography>
        <Paper sx={{ height: 250, overflow: 'auto', borderRadius: 2 }}>
          <List dense sx={{ p: 1 }}>
            {selected.length === 0 ? (
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="body2" color="textSecondary">None selected</Typography>
              </Box>
            ) : (
              selected.map((item) => (
                <ListItem key={item.id} sx={{ border: `1px solid ${grey[300]}`, mb: 0.5 }}>
                  <IconButton edge="start" color="error" size="small" onClick={() => onRemove(item.id)} sx={{ mr: 1 }}>
                    <Close fontSize="small" />
                  </IconButton>
                  <ListItemText primary={item.name} secondary={renderSecondary(item)} />
                </ListItem>
              ))
            )}
          </List>
        </Paper>
      </Box>
    </Box>
  );

  return (
    <Form
      embedded
      hideTitle
      onSubmit={handleSubmit}
      title={defaultValues ? 'Edit Fleet Trailer' : 'Add Fleet Trailer'}
      subtitle="Configure trailer details, GPS device, and optional asset or kit attachments."
      submitLabel={defaultValues ? 'Update Fleet' : 'Save Fleet'}
      onCancel={onCancel}
      isLoading={isSubmitting}
    >
      <FormSection>
        <SectionTitle>Location &amp; Details</SectionTitle>
        <SectionDescription>Select the location and enter trailer information.</SectionDescription>
        <FormGrid $columns={1}>
          <LocationSelect
            required
            disabled={!!defaultValues}
            ovs={ovs}
            selectedOvs={selectedOvs || defaultValues?.OVID || ''}
            defaultName="Select Location"
            onChange={(newOvs: any) => {
              if (defaultValues) return;
              setSelectedOvs(newOvs);
              setFormData((prev) => ({ ...prev, ovid: newOvs ? Number(newOvs) : undefined }));
              setSelectedAssets([]);
              setSelectedKits([]);
            }}
          />
          <FormTextField label="Name" name="name" value={formData.name} onChange={handleChange} required placeholder="Enter trailer name" />
          <FormTextField label="Description" name="description" value={formData.description} onChange={handleChange} required placeholder="Enter trailer description" />
          <FormTextField label="License Plate" name="license_plate" value={formData.license_plate} onChange={handleChange} required placeholder="Enter license plate number" />
        </FormGrid>
      </FormSection>

      <FormSection>
        <SectionTitle>GPS Device</SectionTitle>
        <FormSelect
          id="devices"
          label="GPS Device"
          value={selectedDevice || ''}
          onChange={handleDeviceChange}
          placeholder="Select a Device"
        >
          {devices?.map((device: any) => {
            const currentSelectedDeviceId = defaultValues?.gps_device_id || selectedDevice;
            const isAssigned = device.asset_id !== null || device.fleet_id !== null;
            const isSelectedDevice = device.device_id === currentSelectedDeviceId;
            const isDisabled = isAssigned && !isSelectedDevice;
            return (
              <option key={device.device_id} value={device.device_id} disabled={isDisabled}>
                {device.name || 'Unknown Device'}{isDisabled ? ' (Assigned)' : ''}
              </option>
            );
          })}
        </FormSelect>
      </FormSection>

      {(selectedOvs || (defaultValues && (defaultValues.assets?.length > 0 || defaultValues.kits?.length > 0))) &&
        (filteredAssets.length > 0 || filteredKits.length > 0 || selectedAssets.length > 0 || selectedKits.length > 0) && (
        <FormSection>
          <SectionTitle>Attachments</SectionTitle>
          <SectionDescription>Optionally attach assets or kits to this trailer.</SectionDescription>
          <FormControl fullWidth size="small" sx={selectFocusSx}>
            <InputLabel>Attachment Type</InputLabel>
            <Select
              value={attachmentType}
              onChange={(e) => setAttachmentType(e.target.value as 'asset' | 'kit' | '')}
              label="Attachment Type"
            >
              <MenuItem value="">None</MenuItem>
              {(filteredAssets.length > 0 || selectedAssets.length > 0) && <MenuItem value="asset">Asset</MenuItem>}
              {(filteredKits.length > 0 || selectedKits.length > 0) && <MenuItem value="kit">Kit</MenuItem>}
            </Select>
          </FormControl>

          {attachmentType === 'asset' && (filteredAssets.length > 0 || selectedAssets.length > 0) && (
            renderPickerList(
              searchFilteredAssets,
              selectedAssets,
              handleAddAsset,
              handleRemoveAsset,
              assetSearchTerm,
              setAssetSearchTerm,
              'Search assets by name or serial number',
              'No assets found',
              (asset) => <Box sx={{ color: COLORS.teal }}>{asset.serial_number ? `SN: ${asset.serial_number}` : 'No SN'}</Box>,
            )
          )}

          {attachmentType === 'kit' && (filteredKits.length > 0 || selectedKits.length > 0) && (
            renderPickerList(
              searchFilteredKits,
              selectedKits,
              handleAddKit,
              handleRemoveKit,
              kitSearchTerm,
              setKitSearchTerm,
              'Search kits by name or description',
              'No kits found',
              (kit) => kit.description ? <Box sx={{ color: COLORS.teal }}>{kit.description}</Box> : null,
            )
          )}
        </FormSection>
      )}

      {selectedOvs && filteredAssets.length === 0 && filteredKits.length === 0 && selectedAssets.length === 0 && selectedKits.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Typography variant="body2" color="textSecondary">
            No assets or kits available for the selected location
          </Typography>
        </Box>
      )}
    </Form>
  );
};
